import React from "react";
import { ResponsiveWrapper } from '@nivo/core';
import PropTypes from 'prop-types';

import Node from "./components/Node";
import Link from "./components/Link";
import NodeTooltip from "./components/NodeTooltip";
import LinkTooltip from "./components/LinkTooltip";
import { SankeyProvider } from "./context";
import { useSankey, useZoom } from '../hooks'

const propTypes = {
  data: PropTypes.any,
  width: PropTypes.number,
  height: PropTypes.number,
  containerId: PropTypes.string,
}

const Sankey = ({ data, width, height, containerId }) => {
  const { nodeLinks, colorScheme, sankey } = useSankey(width, height, data);
  const { pathWays: { [data?.largerPath]: largerPath } } = data;
  const fullHeight = height + 70;

  useZoom(containerId, width, fullHeight);

  if (!nodeLinks.nodes.length)
    return null

  sankey(nodeLinks);

  const qColumns = nodeLinks.nodes[largerPath?.nodes[largerPath?.nodes.length - 1]]?.page !== 0 ? largerPath?.nodes.length + 1 : largerPath?.nodes.length;

  const reorganizeGraph = largerPath?.nodes.length !== qColumns;

  const headerHeight = 50;
  const columnWidth = width / qColumns
  const translateY = headerHeight + 20;
  const scaleX = columnWidth * (qColumns - (reorganizeGraph ? 2 : 1)) / width;
  const scaleY = (fullHeight - translateY) / fullHeight;

  return (
    <>
      <svg width={width} height={fullHeight}>
        <g className="root">
          <g transform={`scale(.98)`} transform-origin="center center">
            <g className="header">
              {largerPath?.nodes.map((nodeIndex, i) => (
                <g
                  key={i}
                  transform={`translate(${columnWidth * i})`}
                  strokeWidth={1}
                  strokeOpacity={0.1}
                  strokeDasharray="5,5"
                  stroke="#6c757d"
                  fill={i === 0 || i === largerPath?.nodes.length - 1 && largerPath?.nodes.length === qColumns ? "#6c757d" : "#fff"}
                  fillOpacity={0.1}
                >
                  <text
                    fill="#6c757d"
                    fillOpacity={0.5}
                    x={columnWidth / 2}
                    y={headerHeight / 2}
                    strokeWidth={0}
                    textAnchor="middle"
                    fontWeight="bold"
                  >
                    {i === 0 ? 'Profile' : nodeLinks.nodes[nodeIndex]?.page === 0 && largerPath?.nodes.length === qColumns ? 'Finish' : `Slot ${i}`}
                  </text>
                  <rect
                    width={columnWidth}
                    height={headerHeight}
                  />
                </g>
              ))}
              {reorganizeGraph && (
                <g
                  key={qColumns}
                  transform={`translate(${columnWidth * (qColumns - 1)})`}
                  strokeWidth={1}
                  strokeOpacity={0.1}
                  strokeDasharray="5,5"
                  stroke="#6c757d"
                  fill={"#6c757d"}
                  fillOpacity={0.1}
                >
                  <text
                    fill="#6c757d"
                    fillOpacity={0.5}
                    x={columnWidth / 2}
                    y={headerHeight / 2}
                    strokeWidth={0}
                    textAnchor="middle"
                    fontWeight="bold"
                  >
                    Finish
                  </text>
                  <rect
                    width={columnWidth}
                    height={headerHeight}
                  />
                </g>
              )}
            </g>
            <g className="content" transform={`translate(0 ${translateY})`}>
              <g className="columns">
                {largerPath?.nodes.map((nodeIndex, i) => (
                  <g
                    key={i}
                    transform={`translate(${columnWidth * i} ${headerHeight - translateY})`}
                    strokeWidth={1}
                    strokeOpacity={0.1}
                    strokeDasharray="5,5"
                    stroke="#6c757d"
                    fill={i === 0 || i === largerPath?.nodes.length - 1 && largerPath?.nodes.length === qColumns ? "#6c757d" : "#fff"}
                    fillOpacity={i === 0 || i === largerPath?.nodes.length - 1 ? 0.1 : 1}
                  >
                    <rect
                      width={columnWidth}
                      height={fullHeight - headerHeight}
                    />
                  </g>
                ))}
                {reorganizeGraph && (
                  <g
                    key={qColumns}
                    transform={`translate(${columnWidth * (qColumns - 1)} ${headerHeight - translateY})`}
                    strokeWidth={1}
                    strokeOpacity={0.1}
                    strokeDasharray="5,5"
                    stroke="#6c757d"
                    fill={"#6c757d"}
                    fillOpacity={0.1}
                  >
                    <rect
                      width={columnWidth}
                      height={fullHeight - headerHeight}
                    />
                  </g>
                )}
              </g>
              <g className="graph" transform={`scale(${scaleX} ${scaleY}) translate(${reorganizeGraph ? (-columnWidth/2) / scaleX : 0})`} transform-origin="center center">
                <g>
                  {nodeLinks.links.map((d, i) => (
                    <Link
                      key={i}
                      link={d}
                      reorganizeGraph={reorganizeGraph}
                      columnWidth={columnWidth / scaleX}
                      colors={colorScheme}
                      width={d.width * 0.9}
                      sankeyWidth={width}
                      sankeyHeight={fullHeight}
                    />
                  ))}
                </g>
                <g>
                  {nodeLinks.nodes.map((d, i) => (
                    <Node
                      key={i}
                      sankeyWidth={width}
                      reorganizeGraph={reorganizeGraph}
                      columnWidth={columnWidth / scaleX}
                      sankeyHeight={fullHeight}
                      colors={colorScheme}
                      node={d}
                    />
                  ))}
                </g>
              </g>
            </g>
          </g>
        </g>
        <LinkTooltip width={420} height={140} />
        <NodeTooltip width={360} height={120} />
        <use href="#tooltip-sankey" />
      </svg>
    </>
  );
};

Sankey.propTypes = propTypes
Sankey.defaultProps = {}

const Responsive = props => <ResponsiveWrapper>
  {({ width }) => <SankeyProvider>
    <Sankey width={width} height={600} {...props} />
  </SankeyProvider>
  }
</ResponsiveWrapper>

export default Responsive;
